import type { Difficulty, Direction, Puzzle, Move, RobotColor, TargetColor, WallSpec } from './types.js';

const ROBOT_ORDER: RobotColor[] = ['red', 'blue', 'green', 'yellow'];

const ROBOT_LETTER: Record<RobotColor, string> = {
  red: 'R', blue: 'B', green: 'G', yellow: 'Y', silver: 'S',
};
const LETTER_TO_ROBOT: Record<string, RobotColor> = {
  R: 'red', B: 'blue', G: 'green', Y: 'yellow',
};

const DIR_LETTER: Record<Direction, string> = { N: 'U', S: 'D', E: 'R', W: 'L' };
const LETTER_TO_DIR: Record<string, Direction> = { U: 'N', D: 'S', R: 'E', L: 'W' };

const TARGET_COLOR_ORDER: TargetColor[] = ['red', 'blue', 'green', 'yellow', 'any'];

function setBit(bytes: Uint8Array, bit: number): void {
  bytes[bit >> 3] |= 1 << (bit & 7);
}

function getBit(bytes: Uint8Array, bit: number): boolean {
  return ((bytes[bit >> 3] >> (bit & 7)) & 1) === 1;
}

/**
 * Encodes a Puzzle into a compact base64 string (~108 chars for a 16×16 puzzle).
 * Metadata fields (id, name, author, note) are NOT included — store them as
 * separate DB columns and pass them back to decodePuzzle via the `meta` argument.
 *
 * `minMoves` IS embedded when present (version 2): a trailing byte holds the par,
 * clamped to 0–255. Version 1 strings (no par) still decode correctly, and a `meta`
 * value always overrides the embedded one.
 *
 * Walls are normalized to N/W form during encoding, so a round-trip may change
 * { x, y, side: 'S' } → { x, y+1, side: 'N' }, but the wall geometry is preserved.
 */
export function encodePuzzle(puzzle: Puzzle): string {
  const G = puzzle.gridSize;
  const wallBytes = Math.ceil((2 * G * G) / 8);
  const wallBits = new Uint8Array(wallBytes);

  for (const { x, y, side } of puzzle.walls) {
    let nx = x, ny = y, ns: 'N' | 'W';
    if (side === 'N') { ns = 'N'; }
    else if (side === 'W') { ns = 'W'; }
    else if (side === 'S') { ns = 'N'; ny = y + 1; }
    else { ns = 'W'; nx = x + 1; } // 'E'

    // Skip border walls — they are always implicitly present
    if (ns === 'N' && ny === 0) continue;
    if (ns === 'W' && nx === 0) continue;
    if (ny >= G || nx >= G) continue;

    const cellIdx = ny * G + nx;
    setBit(wallBits, ns === 'N' ? cellIdx * 2 : cellIdx * 2 + 1);
  }

  let robotMask = 0;
  for (let i = 0; i < ROBOT_ORDER.length; i++) {
    if (puzzle.robots.some(r => r.id === ROBOT_ORDER[i])) robotMask |= 1 << i;
  }

  // Embed par as a trailing byte (version 2) only when we have one.
  const hasMinMoves = puzzle.minMoves != null;
  const robotCount = puzzle.robots.length;
  const buf = new Uint8Array(
    4 + wallBytes + robotCount + puzzle.targets.length * 2 + (hasMinMoves ? 1 : 0),
  );

  buf[0] = hasMinMoves ? 2 : 1; // version
  buf[1] = G;
  buf[2] = robotMask;
  buf[3] = puzzle.targets.length;
  buf.set(wallBits, 4);

  let offset = 4 + wallBytes;
  for (const color of ROBOT_ORDER) {
    const robot = puzzle.robots.find(r => r.id === color);
    if (robot) buf[offset++] = (robot.position.x << 4) | robot.position.y;
  }

  for (const target of puzzle.targets) {
    buf[offset++] = (target.position.x << 4) | target.position.y;
    buf[offset++] = TARGET_COLOR_ORDER.indexOf(target.color);
  }

  if (hasMinMoves) {
    buf[offset++] = Math.max(0, Math.min(255, puzzle.minMoves!));
  }

  let binary = '';
  for (let i = 0; i < buf.length; i++) binary += String.fromCharCode(buf[i]);
  return btoa(binary);
}

export interface PuzzleMeta {
  id?: string;
  name?: string;
  author?: string;
  date?: string;
  note?: string;
  minMoves?: number;
}

export function decodePuzzle(encoded: string, meta: PuzzleMeta = {}): Puzzle {
  const binary = atob(encoded);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i);

  const version = buf[0];
  const G = buf[1];
  const robotMask = buf[2];
  const targetCount = buf[3];
  const wallBytes = Math.ceil((2 * G * G) / 8);
  const wallBits = buf.slice(4, 4 + wallBytes);

  const walls: WallSpec[] = [];
  for (let y = 0; y < G; y++) {
    for (let x = 0; x < G; x++) {
      const cellIdx = y * G + x;
      if (y > 0 && getBit(wallBits, cellIdx * 2)) walls.push({ x, y, side: 'N' });
      if (x > 0 && getBit(wallBits, cellIdx * 2 + 1)) walls.push({ x, y, side: 'W' });
    }
  }

  let offset = 4 + wallBytes;
  const robots = [];
  for (let i = 0; i < ROBOT_ORDER.length; i++) {
    if (robotMask & (1 << i)) {
      const byte = buf[offset++];
      robots.push({ id: ROBOT_ORDER[i], position: { x: byte >> 4, y: byte & 0xf } });
    }
  }

  const targets = [];
  for (let i = 0; i < targetCount; i++) {
    const posByte = buf[offset++];
    const colorIdx = buf[offset++];
    targets.push({ position: { x: posByte >> 4, y: posByte & 0xf }, color: TARGET_COLOR_ORDER[colorIdx] });
  }

  // Version 2 appends a par byte. A meta value (if any) takes precedence.
  let embeddedMinMoves: number | undefined;
  if (version >= 2 && offset < buf.length) embeddedMinMoves = buf[offset++];
  const minMoves = meta.minMoves ?? embeddedMinMoves;

  return {
    id: meta.id ?? '',
    name: meta.name ?? '',
    ...(meta.author !== undefined && { author: meta.author }),
    ...(meta.date !== undefined && { date: meta.date }),
    ...(meta.note !== undefined && { note: meta.note }),
    ...(minMoves !== undefined && { minMoves }),
    gridSize: G,
    walls,
    robots,
    targets,
  };
}

export interface CompactPuzzle {
  id: string;
  name: string;
  author?: string;
  date?: string;
  note?: string;
  minMoves?: number;
  encoded: string;
  gridSize?: number;
  numTargets?: number;
  difficulty?: Difficulty;
}

export function toCompact(puzzle: Puzzle): CompactPuzzle {
  return {
    id: puzzle.id,
    name: puzzle.name,
    ...(puzzle.author != null && { author: puzzle.author }),
    ...(puzzle.date != null && { date: puzzle.date }),
    ...(puzzle.note != null && { note: puzzle.note }),
    ...(puzzle.minMoves != null && { minMoves: puzzle.minMoves }),
    encoded: encodePuzzle(puzzle),
    gridSize: puzzle.gridSize,
    numTargets: puzzle.targets.length,
  };
}

export function fromCompact(compact: CompactPuzzle): Puzzle {
  return decodePuzzle(compact.encoded, {
    id: compact.id,
    name: compact.name,
    author: compact.author,
    date: compact.date,
    note: compact.note,
    minMoves: compact.minMoves,
  });
}

/** Encodes a move sequence as "BU-RR-BR-YD-GL-BD". */
export function encodeMoves(moves: Move[]): string {
  return moves.map(m => ROBOT_LETTER[m.robotId] + DIR_LETTER[m.direction]).join('-');
}

/** Parses a move sequence string back to { robotId, direction } pairs. */
export function decodeMoves(seq: string): Array<{ robotId: RobotColor; direction: Direction }> {
  if (!seq) return [];
  return seq.split('-').map(token => ({
    robotId: LETTER_TO_ROBOT[token[0]],
    direction: LETTER_TO_DIR[token[1]],
  }));
}
